/**
 * Gallery axis strategy for 'step' mode.
 *
 * Displays one row per step, with run and index controlled via sliders/dropdowns.
 */

import {sampleSorted} from '@wandb/weave/common/util/data';

import {getStepFromSliderStep} from '../../utils/stepUtils';
import {GridRow, SlotRegistry, TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {caption, indexSlider, label} from '../utils/slots';
import {GalleryAxisStrategy} from './GalleryAxisStrategy';

export class StepAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {stepMeta} = input;

    // Use sliderSteps (sampled) if available, otherwise use all steps
    let steps = stepMeta.sliderSteps ?? stepMeta.steps;

    // Sample steps if we exceed the gallery item limit
    if (maxItems && steps.length > maxItems) {
      steps = sampleSorted(steps, maxItems);
    }

    return steps;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, currentIndex, config, allMasks} = input;
    const {mediaKeys} = config;

    if (!mediaKeys || mediaKeys.length === 0) {
      return [];
    }

    // Use first run for step axis
    const run = runs[0];
    if (!run) {
      return [];
    }

    const runStepMeta = stepMeta.runs[0];
    const rows: GridRow[] = [];

    for (const stepValue of axisValues) {
      // If we're using sliderSteps, map the slider value to a step number
      // Otherwise, stepValue is already a step number
      let step: number;
      let sliderStepValue: number | undefined;

      if (stepMeta.sliderSteps && stepMeta.sliderSteps.length > 0) {
        // This is a slider value, need to map it to a step
        // IMPORTANT: Always snap for tile lookup, regardless of snapToExistingStep
        // The snapToExistingStep setting only affects slider display, not tile lookup
        const result = getStepFromSliderStep(
          runStepMeta,
          stepValue,
          true // Always snap for tile lookup
        );
        step = result.step;
        sliderStepValue = result.sliderStep;
      } else {
        // This is already a step number
        step = stepValue;
      }

      const cells = createCellsForMediaKeys(
        run,
        step,
        currentIndex,
        mediaKeys,
        config,
        allMasks,
        sliderStepValue
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `step-${stepValue}`,
          rowLabel: `Step ${stepValue}`,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(): SlotRegistry {
    // Step axis: steps shown on each tile, run and index are controlled
    return {
      'tile-header': [label('step')],
      'tile-footer': [caption()],
      'content-footer': [indexSlider()],
    };
  }
}
